<script lang="ts" setup>
import type {
  ProductFragment,
  ConfigurableProductFragment,
  GroupedProductFragment,
  CartItemInput,
  ConfigurableProductCartItemInput,
  StockQuery,
} from '#graphql-operations'

export interface Props {
  product: ProductFragment | ConfigurableProductFragment | GroupedProductFragment
}
const { pending } = useCart()
const { addToCart } = useAddToCart()
const hasError = ref(false)
const error = ref(null)
onClickOutside(error, () => {
  hasError.value = false
})

const selectedItems = ref<Record<string, CartItemInput | ConfigurableProductCartItemInput | null>>({})

const props = defineProps<Props>()

const upsell = computed(() => {
  return props.product.relatedProducts?.items?.find((item: any) => item.position.includes('upsell'))
})

// const {
//   result: stockData,
//   load: loadStockData,
//   loading: pendingStock,
// } = useLazyQuery(stockQuery, { sku: props.product.sku })

const stockData = ref<StockQuery | null>(null)
const pendingStock = ref(false)

onMounted(async () => {
  // loadStockData()
  if (props.product.sku) {
    const result = await useGraphqlQuery('stock', { sku: props.product.sku }, useGraphqlCustomerClient())
    // console.log('result', result)

    stockData.value = result
  }
})

const productWithStock = computed(() => {
  const result: ProductFragment | ConfigurableProductFragment | GroupedProductFragment = { ...props.product }
  if (stockData.value?.products?.items?.[0]?.variants?.length) {
    result.variants = result.variants.map((variant, index) => ({ ...variant, product: { ...variant.product, ...stockData.value?.products?.items?.[0]?.variants[index].product } }))
  }
  if (stockData.value?.products?.items?.[0]?.stock_status) {
    result.stockItem = stockData.value?.products?.items?.[0].stockItem
    result.stock_left = stockData.value?.products?.items?.[0].stock_left
    result.stock_status = stockData.value?.products?.items?.[0].stock_status
  }
  if (stockData.value?.products?.items?.[0]?.items) {
    result.items = result.items.map((item, index) => {
      if (stockData.value?.products?.items?.[0]?.items[index]?.product?.variants?.length) {
        return {
          ...item,
          product: {
            ...item.product,
            ...stockData.value?.products?.items?.[0]?.items[index]?.product,
            variants: item.product.variants.map((variant, vIndex) => ({
              ...variant,
              product: { ...variant.product, ...stockData.value?.products?.items?.[0]?.items[index]?.product.variants[vIndex].product },
            })),
          },
        }
      }
      if (stockData.value?.products?.items?.[0]?.items[index]?.product?.stock_status) {
        return { ...item, product: { ...item.product, ...stockData.value.products.items[0].items[index].product } }
      }
      return { ...item }
    })
  }
  return result
})

const variantPrices = computed<Record<string, number>>(() => {
  return productWithStock.value?.items?.reduce((acc: Record<string, number>, item: unknown) => {
    const typedItem = item as { product?: { variants?: Array<{ product: { sku: string, price_range?: { minimum_price?: { final_price?: { value: number } } } } }> } }
    return {
      ...acc,
      ...typedItem?.product?.variants?.reduce((variants: Record<string, number>, variant: unknown) => {
        const typedVariant = variant as { product: { sku: string, price_range?: { minimum_price?: { final_price?: { value: number } } } } }
        variants[typedVariant.product.sku] = typedVariant.product?.price_range?.minimum_price?.final_price?.value || 0
        return variants
      }, {}),
    }
  }, {}) || {}
})

const groupedPrice = computed(() => {
  return Object.values(selectedItems.value).reduce((acc: number, item) => {
    if (props.product?.__typename === 'GroupedProduct' && item?.data?.sku) {
      acc += variantPrices.value?.[item.data.sku] || 0
    }
    return acc
  }, 0)
})

const collect = (item: Record<string, CartItemInput | ConfigurableProductCartItemInput | null>) => {
  selectedItems.value = {
    ...selectedItems.value,
    ...item,
  }
}
const add = () => {
  const item = Object.values(selectedItems.value).filter((item): item is CartItemInput | ConfigurableProductCartItemInput => item !== null)
  if (!item.length) {
    hasError.value = true
  }
  else {
    const product = props.product as ConfigurableProductFragment & { upsell_products?: Array<{ product: ProductFragment }> }
    const context = {
      suppress: false,
      product: product.product,
      configurableOptions: product.configurable_options || [],
      upsell: upsell.value?.items?.map((item: any) => item.product) || [],
    }
    addToCart(item, context)
    hasError.value = false
  }
}
</script>

<template>
  <div class="add-to-cart">
    <ProductSelector
      :product="productWithStock"
      @selected="collect"
    />

    <span
      v-if="hasError"
      ref="error"
      class="required"
    >{{ $t('Please choose a size') }}</span>

    <button
      type="button"
      :disabled="pending || pendingStock"
      class="add-to-cart__cta"
      data-testid="add-to-cart"
      :class="{ 'button--loading': pending || pendingStock }"
      @click.prevent="add"
    >
      <span>{{ $t('Add to cart') }}</span>

      <span v-if="groupedPrice > 0">-</span>
      <i18n-n
        v-if="groupedPrice > 0"
        tag="span"
        :value="groupedPrice"
        :format="{ style: 'currency', currency: 'EUR' }"
      >
        <template #currency="slotProps">
          <span class="price__currency">{{ slotProps.currency }}</span>
        </template>
        <template #integer="slotProps">
          <span class="price__integer">{{ slotProps.integer }}</span>
        </template>
        <template #decimal="slotProps">
          <span class="price__decimal">{{ slotProps.decimal }}</span>
        </template>
        <template #fraction="slotProps">
          <span class="price__fraction">{{ slotProps.fraction }}</span>
        </template>
      </i18n-n>

      <NuxtIcon name="icon:shoppingcart" />

      <NuxtIcon
        v-if="pending || pendingStock"
        name="icon:loader"
      />
    </button>

    <ProductAddToCartSticky
      class="pdp__add-to-cart-sticky"
      :disabled="pending || pendingStock"
      @add-to-cart="add"
    />
  </div>
</template>

<style>
@import url("~layers/components/Product/AddToCart.css");
</style>
